import { create } from 'zustand';

import {
  materialiseVariant,
  propagateSetLayerPatch,
  seedGeometry,
  splitPatch,
} from './propagation';
import { type ReadyGateVerdict, evaluateReadyGate } from './ready-gate';
import { stageFor } from './stage-dimensions';
import {
  type ConceptDisplayStatus,
  type StatusTransition,
  evaluateStatusTransition,
} from './status-vocabulary';
import type {
  AspectRatioToken,
  LayerOverride,
  PropagationResult,
  SetLayer,
  VariantFraming,
  VariantLayerGeometry,
  VariantSetDocument,
  VariantState,
} from './types';

const HISTORY_LIMIT = 50;

const NO_PROPAGATION: PropagationResult = {
  changedVariantIds: [],
  keptOverrides: [],
  markedForReviewIds: [],
};

/**
 * A history entry is the WHOLE SET DOCUMENT, not one variant's element array.
 *
 * This is the deliberate change to the existing single-document history model in
 * `useTextEditorStore` (spec §7.5, flagged again in §2.2). There, `past` holds
 * `AnyElement[]` — one document's elements — so `⌘Z` could only ever undo the
 * selected variant's share of an edit. Here one propagating edit writes the set
 * layer AND flips `needsReview` on several siblings; undoing only the visible
 * variant would leave those flags set and the Ready gate blocked by a change the
 * user just reverted. So the snapshot is `{ layers, variants, mediaId }` and one
 * `⌘Z` undoes the whole propagated change atomically.
 *
 * Selection is NOT part of the snapshot. Undo must not yank the user to another
 * variant; that is navigation, not an edit.
 */
interface Snapshot {
  mediaId: string | null;
  layers: SetLayer[];
  variants: VariantState[];
}

function snapshotOf(doc: VariantSetDocument): Snapshot {
  return { mediaId: doc.mediaId, layers: doc.layers, variants: doc.variants };
}

function restore(doc: VariantSetDocument, snapshot: Snapshot): VariantSetDocument {
  return { ...doc, ...snapshot };
}

interface MutateOptions {
  /**
   * Suppress the history push. Used while a TipTap inline edit is streaming
   * keystrokes, exactly as the existing editor store does — TipTap keeps its own
   * per-character undo and would otherwise flood the 50-entry set history.
   */
  skipHistory?: boolean;
}

export interface VariantSetState {
  document: VariantSetDocument | null;
  selectedVariantId: string | null;
  selectedLayerIds: string[];
  past: Snapshot[];
  future: Snapshot[];
  /** The most recent propagation, so the surface can raise the §7.4 toast. */
  lastPropagation: PropagationResult | null;

  loadSet: (document: VariantSetDocument) => void;
  clear: () => void;

  selectVariant: (variantId: string | null) => void;
  selectLayers: (layerIds: string[]) => void;

  addVariant: (input: {
    id: string;
    aspectRatio: AspectRatioToken;
    mediaId?: string | null;
    followsSetImage?: boolean;
    seedFromVariantId?: string | null;
  }) => void;
  removeVariant: (variantId: string) => void;

  addLayer: (layer: SetLayer) => void;
  deleteLayer: (layerId: string) => void;
  renameLayer: (layerId: string, name: string) => void;
  setLayerVisibility: (layerId: string, visible: boolean) => void;
  bringToFront: (layerId: string) => void;
  sendToBack: (layerId: string) => void;
  bringForward: (layerId: string) => void;
  sendBackward: (layerId: string) => void;
  /**
   * Rewrite the whole stack in one act, bottom-first. A drag or an ⌥↑ can move a
   * layer past several others, and doing that as N adjacent swaps would push N
   * entries onto the history stack — one drag would then need N undos, which is
   * the kind of thing nobody notices until they try to undo it.
   */
  setLayerOrder: (layerIdsBottomFirst: readonly string[]) => void;

  /** A propagating edit. Returns what it did so the caller can name the overrides. */
  updateSetLayer: (
    layerId: string,
    patch: LayerOverride & Partial<Pick<SetLayer, 'name' | 'zIndex' | 'visible'>>,
    options?: MutateOptions,
  ) => PropagationResult;
  /** A per-variant edit to a PROPAGATING property — records an override (§7.4). */
  overrideVariantLayer: (
    variantId: string,
    layerId: string,
    patch: LayerOverride,
    options?: MutateOptions,
  ) => void;
  resetVariantLayerProperty: (variantId: string, layerId: string, property: string) => void;
  resetVariant: (variantId: string) => void;

  /** A per-variant geometry edit. Never propagates, never marks a sibling. */
  updateGeometry: (
    variantId: string,
    layerId: string,
    patch: Partial<VariantLayerGeometry>,
    options?: MutateOptions,
  ) => void;
  setFraming: (variantId: string, framing: VariantFraming) => void;

  setSetImage: (mediaId: string | null) => void;
  setVariantImage: (variantId: string, mediaId: string | null) => void;
  relinkVariantImage: (variantId: string) => void;

  setGenerating: (variantId: string, isGenerating: boolean) => void;
  markNeedsReview: (variantId: string, needsReview: boolean) => void;

  undo: () => void;
  redo: () => void;

  readyGate: () => ReadyGateVerdict;
  requestStatus: (target: ConceptDisplayStatus) => StatusTransition;
  materialise: (variantId: string) => ReturnType<typeof materialiseVariant>;
}

export const useVariantSetStore = create<VariantSetState>((set, get) => {
  /** Every mutation funnels through here so history stays set-level and uniform. */
  function mutate(
    fn: (doc: VariantSetDocument) => VariantSetDocument,
    options?: MutateOptions,
  ): void {
    const state = get();
    const doc = state.document;
    if (!doc) return;
    const next = fn(doc);
    if (next === doc) return;
    set({
      document: next,
      past: options?.skipHistory
        ? state.past
        : [...state.past, snapshotOf(doc)].slice(-HISTORY_LIMIT),
      future: options?.skipHistory ? state.future : [],
    });
  }

  function mapVariant(
    doc: VariantSetDocument,
    variantId: string,
    fn: (variant: VariantState) => VariantState,
  ): VariantSetDocument {
    let touched = false;
    const variants = doc.variants.map((variant) => {
      if (variant.id !== variantId) return variant;
      const next = fn(variant);
      if (next !== variant) touched = true;
      return next;
    });
    return touched ? { ...doc, variants } : doc;
  }

  function reorder(layerId: string, resolveZ: (layers: SetLayer[], current: SetLayer) => number) {
    mutate((doc) => {
      const current = doc.layers.find((l) => l.id === layerId);
      if (!current) return doc;
      const targetZ = resolveZ(doc.layers, current);
      if (targetZ === current.zIndex) return doc;
      const swapped = doc.layers.find((l) => l.id !== layerId && l.zIndex === targetZ);
      const layers = doc.layers.map((layer) => {
        if (layer.id === layerId) return { ...layer, zIndex: targetZ };
        if (swapped && layer.id === swapped.id) return { ...layer, zIndex: current.zIndex };
        return layer;
      });
      return { ...doc, layers };
    });
  }

  return {
    document: null,
    selectedVariantId: null,
    selectedLayerIds: [],
    past: [],
    future: [],
    lastPropagation: null,

    loadSet: (document) =>
      set({
        document,
        past: [],
        future: [],
        lastPropagation: null,
        selectedVariantId:
          [...document.variants].sort((a, b) => a.position - b.position)[0]?.id ?? null,
        selectedLayerIds: [],
      }),

    clear: () =>
      set({
        document: null,
        past: [],
        future: [],
        lastPropagation: null,
        selectedVariantId: null,
        selectedLayerIds: [],
      }),

    selectVariant: (variantId) => set({ selectedVariantId: variantId }),
    selectLayers: (layerIds) => set({ selectedLayerIds: layerIds }),

    addVariant: ({ id, aspectRatio, mediaId, followsSetImage, seedFromVariantId }) =>
      mutate((doc) => {
        if (doc.variants.some((v) => v.aspectRatio === aspectRatio)) return doc;
        const source = seedFromVariantId
          ? doc.variants.find((v) => v.id === seedFromVariantId)
          : undefined;
        const geometry: Record<string, VariantLayerGeometry> = {};
        for (const layer of doc.layers) {
          const from = source?.geometry[layer.id];
          geometry[layer.id] = seedGeometry(
            layer,
            aspectRatio,
            from ? { geometry: from, ratio: source!.aspectRatio } : undefined,
          );
        }
        const follows = followsSetImage ?? mediaId === undefined;
        const variant: VariantState = {
          id,
          aspectRatio,
          position: doc.variants.length,
          mediaId: follows ? doc.mediaId : (mediaId ?? null),
          followsSetImage: follows,
          needsReview: false,
          isGenerating: false,
          version: 1,
          framing: {},
          geometry,
          overrides: {},
        };
        return { ...doc, variants: [...doc.variants, variant] };
      }),

    removeVariant: (variantId) =>
      mutate((doc) => {
        if (!doc.variants.some((v) => v.id === variantId)) return doc;
        return {
          ...doc,
          variants: doc.variants
            .filter((v) => v.id !== variantId)
            .map((v, index) => ({ ...v, position: index })),
        };
      }),

    addLayer: (layer) =>
      mutate((doc) => {
        const maxZ = doc.layers.reduce((max, l) => Math.max(max, l.zIndex), -1);
        const next = { ...layer, zIndex: maxZ + 1 };
        return {
          ...doc,
          layers: [...doc.layers, next],
          variants: doc.variants.map((variant) => ({
            ...variant,
            geometry: {
              ...variant.geometry,
              [next.id]: seedGeometry(next, variant.aspectRatio),
            },
          })),
        };
      }),

    deleteLayer: (layerId) =>
      mutate((doc) => {
        if (!doc.layers.some((l) => l.id === layerId)) return doc;
        return {
          ...doc,
          layers: doc.layers.filter((l) => l.id !== layerId),
          variants: doc.variants.map((variant) => {
            const geometry = { ...variant.geometry };
            const overrides = { ...variant.overrides };
            delete geometry[layerId];
            delete overrides[layerId];
            return { ...variant, geometry, overrides };
          }),
        };
      }),

    renameLayer: (layerId, name) => {
      get().updateSetLayer(layerId, { name });
    },

    setLayerVisibility: (layerId, visible) => {
      get().updateSetLayer(layerId, { visible });
    },

    bringToFront: (layerId) =>
      mutate((doc) => {
        const maxZ = doc.layers.reduce((max, l) => Math.max(max, l.zIndex), 0);
        const current = doc.layers.find((l) => l.id === layerId);
        if (!current || current.zIndex === maxZ) return doc;
        return {
          ...doc,
          layers: doc.layers.map((l) => (l.id === layerId ? { ...l, zIndex: maxZ + 1 } : l)),
        };
      }),

    sendToBack: (layerId) =>
      mutate((doc) => {
        const minZ = doc.layers.reduce((min, l) => Math.min(min, l.zIndex), 0);
        const current = doc.layers.find((l) => l.id === layerId);
        if (!current || current.zIndex === minZ) return doc;
        return {
          ...doc,
          layers: doc.layers.map((l) => (l.id === layerId ? { ...l, zIndex: minZ - 1 } : l)),
        };
      }),

    bringForward: (layerId) =>
      reorder(layerId, (layers, current) => {
        const above = layers
          .filter((l) => l.id !== current.id && l.zIndex > current.zIndex)
          .sort((a, b) => a.zIndex - b.zIndex)[0];
        return above ? above.zIndex : current.zIndex;
      }),

    sendBackward: (layerId) =>
      reorder(layerId, (layers, current) => {
        const below = layers
          .filter((l) => l.id !== current.id && l.zIndex < current.zIndex)
          .sort((a, b) => b.zIndex - a.zIndex)[0];
        return below ? below.zIndex : current.zIndex;
      }),

    setLayerOrder: (layerIdsBottomFirst) =>
      mutate((doc) => {
        // Every layer must be accounted for, or the stack would silently gain a
        // duplicate zIndex and the paint order would depend on array position.
        if (layerIdsBottomFirst.length !== doc.layers.length) return doc;
        const nextZ = new Map(layerIdsBottomFirst.map((id, index) => [id, index]));
        if (doc.layers.some((layer) => !nextZ.has(layer.id))) return doc;
        if (doc.layers.every((layer) => layer.zIndex === nextZ.get(layer.id))) return doc;
        return {
          ...doc,
          layers: doc.layers.map((layer) => ({ ...layer, zIndex: nextZ.get(layer.id)! })),
        };
      }),

    updateSetLayer: (layerId, patch, options) => {
      const { propagating, perVariant } = splitPatch(patch as Record<string, unknown>);
      const stray = Object.keys(perVariant);
      if (stray.length > 0) {
        throw new Error(
          `${stray.join(', ')} is per-ratio and must never propagate. Use updateGeometry.`,
        );
      }
      if (Object.keys(propagating).length === 0) return NO_PROPAGATION;
      let result = NO_PROPAGATION;
      mutate((doc) => {
        const outcome = propagateSetLayerPatch(
          doc,
          layerId,
          propagating as LayerOverride,
          get().selectedVariantId,
        );
        result = outcome.result;
        return outcome.doc;
      }, options);
      set({ lastPropagation: result });
      return result;
    },

    overrideVariantLayer: (variantId, layerId, patch, options) => {
      const { perVariant } = splitPatch(patch as Record<string, unknown>);
      const stray = Object.keys(perVariant);
      if (stray.length > 0) {
        throw new Error(
          `Geometry cannot be overridden — ${stray.join(', ')} is per-ratio already. Use updateGeometry.`,
        );
      }
      mutate(
        (doc) =>
          mapVariant(doc, variantId, (variant) => ({
            ...variant,
            overrides: {
              ...variant.overrides,
              [layerId]: { ...variant.overrides[layerId], ...patch },
            },
          })),
        options,
      );
    },

    resetVariantLayerProperty: (variantId, layerId, property) =>
      mutate((doc) =>
        mapVariant(doc, variantId, (variant) => {
          const existing = variant.overrides[layerId];
          if (!existing || !(property in existing)) return variant;
          const next = { ...existing };
          delete next[property as keyof LayerOverride];
          const overrides = { ...variant.overrides };
          if (Object.keys(next).length === 0) delete overrides[layerId];
          else overrides[layerId] = next;
          return { ...variant, overrides };
        }),
      ),

    resetVariant: (variantId) =>
      mutate((doc) =>
        mapVariant(doc, variantId, (variant) =>
          Object.keys(variant.overrides).length === 0 ? variant : { ...variant, overrides: {} },
        ),
      ),

    updateGeometry: (variantId, layerId, patch, options) => {
      const { propagating } = splitPatch(patch as Record<string, unknown>);
      const stray = Object.keys(propagating);
      if (stray.length > 0) {
        throw new Error(
          `${stray.join(', ')} is a set-level property and must not be written per ratio. Use updateSetLayer.`,
        );
      }
      mutate(
        (doc) =>
          mapVariant(doc, variantId, (variant) => {
            const layer = doc.layers.find((l) => l.id === layerId);
            const base =
              variant.geometry[layerId] ??
              (layer ? seedGeometry(layer, variant.aspectRatio) : undefined);
            if (!base) return variant;
            return {
              ...variant,
              geometry: { ...variant.geometry, [layerId]: { ...base, ...patch } },
            };
          }),
        options,
      );
    },

    setFraming: (variantId, framing) =>
      mutate((doc) =>
        mapVariant(doc, variantId, (variant) => ({
          ...variant,
          framing: { ...variant.framing, ...framing },
          followsSetImage: false,
        })),
      ),

    setSetImage: (mediaId) =>
      mutate((doc) => ({
        ...doc,
        mediaId,
        variants: doc.variants.map((variant) =>
          variant.followsSetImage ? { ...variant, mediaId } : variant,
        ),
      })),

    setVariantImage: (variantId, mediaId) =>
      mutate((doc) =>
        mapVariant(doc, variantId, (variant) => ({
          ...variant,
          mediaId,
          followsSetImage: false,
        })),
      ),

    relinkVariantImage: (variantId) =>
      mutate((doc) =>
        mapVariant(doc, variantId, (variant) => ({
          ...variant,
          mediaId: doc.mediaId,
          followsSetImage: true,
          framing: {},
        })),
      ),

    setGenerating: (variantId, isGenerating) =>
      mutate((doc) => mapVariant(doc, variantId, (variant) => ({ ...variant, isGenerating })), {
        skipHistory: true,
      }),

    markNeedsReview: (variantId, needsReview) =>
      mutate((doc) => mapVariant(doc, variantId, (variant) => ({ ...variant, needsReview }))),

    undo: () => {
      const state = get();
      const doc = state.document;
      if (!doc || state.past.length === 0) return;
      const previous = state.past[state.past.length - 1];
      set({
        document: restore(doc, previous),
        past: state.past.slice(0, -1),
        future: [snapshotOf(doc), ...state.future].slice(0, HISTORY_LIMIT),
      });
    },

    redo: () => {
      const state = get();
      const doc = state.document;
      if (!doc || state.future.length === 0) return;
      const next = state.future[0];
      set({
        document: restore(doc, next),
        past: [...state.past, snapshotOf(doc)].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
      });
    },

    readyGate: () => evaluateReadyGate(get().document),
    requestStatus: (target) => evaluateStatusTransition(target, get().document),
    materialise: (variantId) => {
      const doc = get().document;
      return doc ? materialiseVariant(doc, variantId) : [];
    },
  };
});

/** Stage size for the selected variant — the canvas needs it every frame. */
export function selectStageSize(state: VariantSetState) {
  const variant = state.document?.variants.find((v) => v.id === state.selectedVariantId);
  return variant ? stageFor(variant.aspectRatio) : null;
}
